import * as React from 'react';
import { IRequest } from 'common/schema/request/Request';
import { RequestTableCellTitle } from 'client/components/schema/requests/table/RequestTableCellTitle';
import { ProcedureTableCellRecipient } from 'client/components/schema/procedures/table/ProcedureTableCellRecipient';
import { ColumnProps } from 'antd/lib/table';
import { RequestTableCellActions } from 'client/components/schema/requests/table/RequestTableCellActions';
import { useTranslation } from 'react-i18next';
import { RequestTableAdminActions } from 'client/components/schema/requests/table/RequestTableAdminActions';
import { ISODate } from 'client/ui/display/date/ISODate';
import { RequestStateTag } from 'client/components/schema/requests/status/RequestStateTag';
import { RequestLogic } from 'common/schema/RequestLogic';
import { RequestPointsItem } from './components/RequestPointsItem';
import { IProcedure } from 'common/schema/Procedure';
import { IUser } from 'common/schema/User';
import {
  useCurrentUser,
  usePermissions
} from 'client/components/auth/AuthModule';

export function useRequestColumns() {
  const { t } = useTranslation();

  return {
    recipients: {
      title: '',
      dataIndex: 'recipients',
      key: 'recipients',
      responsive: ['md'],
      width: 54,
      render: (_, record) => (
        <ProcedureTableCellRecipient recipient={record.procedure.recipient} />
      )
    } as ColumnProps<IRequest>,
    author: {
      title: 'Autore',
      dataIndex: 'authorName',
      key: 'authorName',
      render: (_, record) =>
        record.citizen?.fullName ??
        RequestLogic.getAuthorFullName(record) ??
        '-'
    } as ColumnProps<IRequest>,
    state: {
      title: 'Stato',
      dataIndex: 'state',
      key: 'state',
      render: (_, record) => <RequestStateTag status={record.state} />
    } as ColumnProps<IRequest>,
    fiscalId: {
      title: 'Codice Fiscale',
      dataIndex: 'fiscalCode',
      key: 'fiscalCode',
      render: (_, record) =>
        record.citizen?.localFiscalNumber ?? record.citizen?.fiscalNumber ?? '-'
    } as ColumnProps<IRequest>,
    sentAt: {
      title: 'Data invio richiesta',
      dataIndex: 'protocolDate',
      key: 'protocolDate',
      render: (_, record) => <ISODate date={record.protocolDate} />
    } as ColumnProps<IRequest>,
    totalRulesPoints: {
      title: 'Punteggio',
      dataIndex: 'total',
      key: 'total',
      width: 120,
      render: (_, record) => {
        const rules = record.totalRulesPoints ?? 0;
        const commission = record.totalCommissionPoints ?? 0;
        return rules + commission;
      }
    } as ColumnProps<IRequest>,
    commissionPoints: {
      title: 'Commissione',
      dataIndex: 'commission',
      key: 'commission',
      width: 120,
      render: (_, record) => {
        return record.totalCommissionPoints ?? 0;
      }
    } as ColumnProps<IRequest>,
    code: {
      title: 'Codice',
      dataIndex: 'code',
      key: 'code'
    },
    title: {
      title: t('procedures.list.name', 'Domanda'),
      dataIndex: 'title',
      key: 'title',
      render: (_, record) => <RequestTableCellTitle request={record} />
    } as ColumnProps<IRequest>
  };
}

type RequestColumns = {
  [k in keyof ReturnType<typeof useRequestColumns>]: ColumnProps<IRequest>;
};

export function useFrontRequestColumns(): ColumnProps<IRequest>[] {
  const columns = useRequestColumns();
  const { t } = useTranslation();
  return [
    columns.recipients,
    columns.title, // Azioni Utente Front
    {
      title: t('procedures.list.actions', 'Azioni'),
      key: 'actions',
      responsive: ['md'],
      width: 110,
      render: (_, record) => <RequestTableCellActions request={record} />
    } as ColumnProps<IRequest>
  ];
}

export function useAdminRequestColumns(
  procedure: IProcedure | null | undefined
): ColumnProps<IRequest>[] {
  const columns: RequestColumns = useRequestColumns();

  const permissions = usePermissions();

  return [
    // columns.recipients,
    // columns.title,
    columns.code,
    columns.state,
    columns.author,
    columns.fiscalId,
    columns.sentAt,
    // Request Points
    ...(procedure?.activeScoring && permissions.has('request.rank.read')
      ? [
          {
            title: 'Punteggio',
            dataIndex: 'points',
            key: 'points',
            render: (_, record) => (
              <RequestPointsItem request={record} procedure={procedure} />
            )
          } as ColumnProps<IRequest>
        ]
      : []),
    // columns.commissionPoints,
    // Azioni ADMIN
    {
      title: 'Azioni',
      key: 'actions',
      width: 130,
      render: (_, record) => <RequestTableAdminActions request={record} />
    } as ColumnProps<IRequest>
  ];
}
